/**
 * L'UN storefront overlays.
 *
 * The cookie banner is rendered by the Consentik app inside an open shadow root, so page CSS
 * cannot reach it. This script adopts a stylesheet into that root once the host exists.
 * Markup, copy and consent behaviour are untouched; only colour, type, spacing and shape change.
 * Reject all and Accept share one identical style (size, weight, colour, hover): neither is nudged.
 * Companion styles for light-DOM overlays live in assets/lun-overlays.css.
 */

(() => {
const HOST_ID = 'cst-shadow-host';

const CONSENT_CSS = `
  /* Until this sheet is in place the host is invisible (assets/lun-overlays.css); no transition may play the
     switch from the app's default look to this one. */
  :host(:not([data-lun-styled])) *,
  :host(:not([data-lun-styled])) *::before,
  :host(:not([data-lun-styled])) *::after {
    transition: none !important;
    animation: none !important;
  }

  :host {
    --lun-paper: #fbf6f0;
    --lun-ink: #3b1f27;
    --lun-ink-soft: rgb(59 31 39 / 0.72);
    --lun-rose: #5a343e;
    --lun-pink: #f4869f;
    --lun-pink-deep: #d95f7f;
    --lun-blush: #f9dce2;
    --lun-hair: rgb(90 52 62 / 0.18);
    --lun-label: 'Inter', system-ui, sans-serif;
    --lun-body: 'Merriweather', Georgia, serif;
  }

  /* The wrapper is the fixed element; the card sits inside it in normal flow */
  #cst-root #cookies-wrapper {
    position: fixed !important;
    inset: auto 0.75rem 0.75rem 0.75rem !important;
    inline-size: auto !important;
    width: auto !important;
    min-width: 0 !important;
    max-inline-size: none !important;
    max-width: none !important;
    block-size: auto !important;
    max-block-size: none !important;
    margin: 0 !important;
    padding: 0 !important;
    transform: none !important;
    background: transparent !important;
    box-shadow: none !important;
    border: 0 !important;
    overflow: visible !important;
    pointer-events: none;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner {
    position: relative !important;
    inset: auto !important;
    inline-size: 100% !important;
    min-inline-size: 0 !important;
    max-inline-size: none !important;
    flex: 1 1 100% !important;
    margin: 0 !important;
    transform: none !important;
    background: var(--lun-paper) !important;
    color: var(--lun-ink) !important;
    border: 1px solid var(--lun-hair) !important;
    border-radius: 1.125rem !important;
    box-shadow: 0 1.25rem 3.5rem rgb(90 52 62 / 0.16) !important;
    pointer-events: auto;
    overflow: hidden;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cb__inner {
    display: grid !important;
    gap: 0.875rem !important;
    margin: 0 !important;
    padding: 1.125rem 1.125rem 0.75rem !important;
    background: transparent !important;
    text-align: start !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cb__text-block,
  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cb__after-text {
    text-align: start !important;
    justify-content: flex-start !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cookie-banner__title {
    text-align: start !important;
    margin: 0 0 0.4rem !important;
    font-family: var(--lun-label) !important;
    font-size: 0.66rem !important;
    font-weight: 500 !important;
    letter-spacing: 0.26em !important;
    text-transform: uppercase !important;
    color: var(--lun-rose) !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cookie-banner__message {
    text-align: start !important;
    margin: 0 !important;
    font-family: var(--lun-body) !important;
    font-size: 0.75rem !important;
    line-height: 1.65 !important;
    color: var(--lun-ink-soft) !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cookie-banner__policy-links {
    justify-content: flex-start !important;
    text-align: start !important;
    margin: 0.4rem 0 0 !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cookie-banner__link {
    font-family: var(--lun-body) !important;
    font-size: 0.75rem !important;
    color: var(--lun-ink) !important;
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 0.18em !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-action {
    display: grid !important;
    grid-template-columns: 1fr !important;
    gap: 0.25rem !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cookie-banner__actions-right {
    order: 1 !important;
    display: grid !important;
    grid-template-columns: 1fr 1fr !important;
    gap: 0.5rem !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-dismiss,
  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-submit {
    min-block-size: 2.75rem !important;
    margin: 0 !important;
    padding: 0 1rem !important;
    border: 1px solid var(--lun-pink) !important;
    border-radius: 999px !important;
    background: var(--lun-pink) !important;
    color: var(--lun-ink) !important;
    font-family: var(--lun-label) !important;
    font-size: 0.7rem !important;
    font-weight: 500 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    box-shadow: none !important;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  /* Reject all and Accept: one identical hover */
  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-dismiss:hover,
  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-submit:hover {
    background: var(--lun-ink) !important;
    border-color: var(--lun-ink) !important;
    color: var(--lun-paper) !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-preferences {
    order: 2 !important;
    min-block-size: 2.25rem !important;
    margin: 0 !important;
    padding: 0.25rem !important;
    border: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
    color: var(--lun-ink-soft) !important;
    font-family: var(--lun-label) !important;
    font-size: 0.7rem !important;
    letter-spacing: 0.1em !important;
    text-decoration: underline !important;
    text-decoration-thickness: 1px !important;
    text-underline-offset: 0.25em !important;
    justify-self: center;
    cursor: pointer;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner #cookies-preferences:hover {
    color: var(--lun-ink) !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner .cst-brandmark {
    display: flex !important;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    margin: 0 !important;
    padding: 0 0 0.75rem !important;
    font-family: var(--lun-label) !important;
    font-size: 0.58rem !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: rgb(59 31 39 / 0.55) !important;
    text-decoration: none !important;
  }

  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner button:focus-visible,
  #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner a:focus-visible {
    outline: 2px solid var(--lun-pink-deep) !important;
    outline-offset: 2px !important;
  }

  /* Manage preferences dialog */
  #cst-root #cookies-popup.prefs-modal {
    background: var(--lun-paper) !important;
    color: var(--lun-ink) !important;
    border: 1px solid var(--lun-hair) !important;
    border-radius: 1.125rem !important;
    box-shadow: 0 1.25rem 3.5rem rgb(90 52 62 / 0.16) !important;
    font-family: var(--lun-body) !important;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__head,
  #cst-root #cookies-popup.prefs-modal .prefs-modal__body,
  #cst-root #cookies-popup.prefs-modal .prefs-modal__bottom {
    background: transparent !important;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__title {
    margin: 0 !important;
    font-family: 'Libre Baskerville', Georgia, serif !important;
    font-size: 1.3rem !important;
    font-weight: 400 !important;
    line-height: 1.25 !important;
    text-align: start !important;
    color: var(--lun-ink) !important;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__intro {
    font-family: var(--lun-body) !important;
    font-size: 0.78rem !important;
    line-height: 1.7 !important;
    text-align: start !important;
    color: var(--lun-ink-soft) !important;
  }

  #cst-root #cookies-popup.prefs-modal .category-item {
    border-top: 1px solid var(--lun-hair) !important;
  }

  #cst-root #cookies-popup.prefs-modal .category-item__name {
    font-family: var(--lun-label) !important;
    font-size: 0.7rem !important;
    font-weight: 500 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    color: var(--lun-ink) !important;
  }

  #cst-root #cookies-popup.prefs-modal .omgCookie {
    background: rgb(90 52 62 / 0.18) !important;
    border: 0 !important;
  }

  #cst-root #cookies-popup.prefs-modal .omgCookie::before,
  #cst-root #cookies-popup.prefs-modal .omgCookie::after {
    background: var(--lun-paper) !important;
    border-color: var(--lun-paper) !important;
  }

  #cst-root #cookies-popup.prefs-modal .omgCookie-input-switch:checked + .omgCookie {
    background: var(--lun-ink) !important;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__close,
  #cst-root #cookies-popup.prefs-modal .cst-close-popup {
    color: var(--lun-ink) !important;
    background: transparent !important;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__close svg,
  #cst-root #cookies-popup.prefs-modal .prefs-modal__close svg path {
    stroke: var(--lun-ink) !important;
    fill: var(--lun-ink) !important;
  }

  /* Save my choices and Accept all share one identical style */
  #cst-root #cookies-popup.prefs-modal .prefs-modal__actions .btn {
    min-block-size: 2.75rem !important;
    padding: 0 1rem !important;
    border: 1px solid var(--lun-pink) !important;
    border-radius: 999px !important;
    background: var(--lun-pink) !important;
    color: var(--lun-ink) !important;
    font-family: var(--lun-label) !important;
    font-size: 0.7rem !important;
    font-weight: 500 !important;
    letter-spacing: 0.16em !important;
    text-transform: uppercase !important;
    box-shadow: none !important;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  #cst-root #cookies-popup.prefs-modal .prefs-modal__actions .btn:hover {
    background: var(--lun-ink) !important;
    border-color: var(--lun-ink) !important;
    color: var(--lun-paper) !important;
  }

  #cst-root #cookies-popup.prefs-modal .cst-brandmark {
    font-family: var(--lun-label) !important;
    font-size: 0.58rem !important;
    letter-spacing: 0.12em !important;
    text-transform: uppercase !important;
    color: rgb(59 31 39 / 0.55) !important;
  }

  #cst-root #cookies-popup.prefs-modal button:focus-visible,
  #cst-root #cookies-popup.prefs-modal a:focus-visible {
    outline: 2px solid var(--lun-pink-deep) !important;
    outline-offset: 2px !important;
  }

  @media (min-width: 750px) {
    #cst-root #cookies-wrapper {
      inset: auto auto 1.25rem 1.25rem !important;
      inline-size: min(25rem, calc(100vw - 2.5rem)) !important;
      width: min(25rem, calc(100vw - 2.5rem)) !important;
      min-width: 0 !important;
      max-width: none !important;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    #cst-root #cookies-wrapper #ot-cookie-banner.cookie-banner button {
      transition: none !important;
    }
  }
`;

const applied = new WeakSet();

/**
 * @param {Element} host
 * @param {string} css
 */
function apply(host, css) {
  const root = host.shadowRoot;
  if (!root || applied.has(root)) return;
  applied.add(root);

  try {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(css);
    // Adopted sheets sort after the root's own styles, so equal specificity resolves in our favour
    root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
  } catch {
    const style = document.createElement('style');
    style.setAttribute('data-lun-overlays', '');
    style.textContent = css;
    root.append(style);
  }
  // Reveal once the new styles have been computed (two frames), so the banner appears already styled.
  requestAnimationFrame(() => requestAnimationFrame(() => host.setAttribute('data-lun-styled', '')));
}

const CART_CSS = `
  :host {
    --cart-bg-color: #fbf6f0;
    --cart-text-color: #3b1f27;
    --cart-divider-color: rgb(90 52 62 / 0.14);
    --cart-top-bar-bg-color: #fbf6f0;
    --cart-top-bar-text-color: #3b1f27;
    --products-upsell-bg: #f9dce2;
    --products-upsell-card-bg: #fbf6f0;
    --cart-button-radius: 999px;
    font-family: 'Inter', system-ui, sans-serif;
  }

  .cart-checkout-link {
    background: #f4869f !important;
    color: #3b1f27 !important;
    border-radius: 999px !important;
    transition: background-color 0.2s ease, color 0.2s ease;
  }

  .cart-checkout-link:hover,
  .cart-checkout-link:focus-visible {
    background: #3b1f27 !important;
    color: #fbf6f0 !important;
  }

  .cart-checkout-link .cart-checkout-text {
    color: inherit !important;
  }

  .cart-products-upsell-button {
    background: #f4869f !important;
    color: #3b1f27 !important;
    border-radius: 999px !important;
  }

  .cart-products-upsell-button:hover,
  .cart-products-upsell-button:focus-visible {
    background: #3b1f27 !important;
    color: #fbf6f0 !important;
  }

  .cart-drawer,
  .cart-body,
  .cart-footer,
  .cart-top-bar {
    background-color: #fbf6f0 !important;
  }

  .cart-top-bar {
    position: relative;
    border-bottom: 0 !important;
  }

  .cart-top-bar::after {
    content: '';
    position: absolute;
    inset-inline: 1.25rem;
    inset-block-end: -6px;
    block-size: 12px;
    background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='40' height='12' viewBox='0 0 40 12'%3E%3Cpath d='M0 6 Q10 -1 20 6 T40 6' fill='none' stroke='%23F4869F' stroke-width='2' stroke-linecap='round'/%3E%3C/svg%3E") repeat-x center / 40px 12px;
    -webkit-mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
    mask-image: linear-gradient(to right, transparent, #000 12%, #000 88%, transparent);
    pointer-events: none;
  }

  .cart-title {
    font-family: 'Libre Baskerville', Georgia, serif;
    font-weight: 400;
  }

  .cart-line-image {
    overflow: hidden;
    border-radius: 1.1rem !important;
    background: #f9dce2 !important;
  }

  .cart-line-image img {
    object-fit: contain;
  }

  .cart-line-title {
    font-family: 'Libre Baskerville', Georgia, serif;
    font-weight: 400;
  }

  .cart-line-quantity {
    border-radius: 999px !important;
  }

  .cart-footer {
    box-shadow: 0 -22px 34px -28px rgb(59 31 39 / 0.45);
  }

  .cart-subtotal-title {
    font-family: 'Libre Baskerville', Georgia, serif;
    font-weight: 400;
  }

  .cart-checkout-link {
    position: relative;
    padding-inline-end: 3.6rem !important;
    font-weight: 600;
  }

  .cart-checkout-icon {
    display: none !important;
  }

  .cart-checkout-link::after {
    content: '';
    position: absolute;
    inset-inline-end: 0.4rem;
    inset-block-start: 50%;
    inline-size: 2.6rem;
    block-size: 2.6rem;
    border-radius: 50%;
    background: #3b1f27 url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23FBF6F0' d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E") center / 1.05rem no-repeat;
    transform: translateY(-50%);
    transition: background-color 0.4s ease, transform 0.5s cubic-bezier(0.2, 0.7, 0.2, 1);
  }

  .cart-checkout-link:hover::after,
  .cart-checkout-link:focus-visible::after {
    background-color: #f4869f;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%233B1F27' d='M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z'/%3E%3C/svg%3E");
    transform: translateY(-50%) rotate(-12deg) scale(1.08);
  }

  .cart-trust-badge {
    color: #5a343e;
  }
`;

/**
 * Style every matching host that has a shadow root, now and whenever the page adds one later
 * (late-loading apps, section re-renders). `apply` ignores roots it has already styled.
 * @param {string} selector @param {string} css
 */
function watch(selector, css) {
  const scan = () => {
    for (const host of document.querySelectorAll(selector)) {
      if (host.shadowRoot) apply(host, css);
    }
  };
  scan();

  // One scan per frame at most: soon enough that the consent banner is styled before it is ever seen.
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

watch('#' + HOST_ID, CONSENT_CSS);
watch('section-store--cart-block', CART_CSS);
})();
